import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetContact } from '../../hooks/contacts/useGetContact';
import { useMarkContactRead } from '../../hooks/contacts/useMarkContactRead';
import { usePermissions } from '../../auth/PermissionsContext';
import WrapperSection from '../../components/global/WrapperSection';
import GlobalBreadcrumbs from '../../components/global/GlobalBreadcrumbs';
import LoadingPage from '../../components/global/LoadingPage';
import Forbidden from '../Forbidden';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ContactDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');
    const { has } = usePermissions();

    const { isLoading, error, data } = useGetContact(id);
    const contact = data?.data || null;
    const { mutate: markRead, isPending: isMarkingRead } = useMarkContactRead();

    if (!has('view-contact')) {
        return <Forbidden />;
    }

    if (isLoading) return <LoadingPage />;
    if (error) return null;

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/contacts')}
                >
                    {isArabic ? 'رجوع' : 'Back'}
                </Button>
                {!contact?.is_read && (
                    <Button
                        variant="contained"
                        onClick={() => markRead(contact.id)}
                        disabled={isMarkingRead}
                    >
                        {isArabic ? 'وضع كمقروء' : 'Mark as Read'}
                    </Button>
                )}
            </Box>

            <Card>
                <CardContent>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="flex-start" spacing={2}>
                            <Box>
                                <Typography variant="h5" fontWeight={700} gutterBottom>
                                    {contact?.subject || (isArabic ? 'رسالة جديدة' : 'New Message')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {isArabic ? 'تفاصيل الرسالة' : 'Message details'}
                                </Typography>
                            </Box>
                            <Chip
                                label={contact?.is_read ? (isArabic ? 'مقروء' : 'Read') : (isArabic ? 'غير مقروء' : 'Unread')}
                                color={contact?.is_read ? 'success' : 'warning'}
                                variant="outlined"
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {isArabic ? 'الاسم' : 'Name'}
                                </Typography>
                                <Typography>{contact?.name || contact?.full_name || '-'}</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {isArabic ? 'البريد الإلكتروني' : 'Email'}
                                </Typography>
                                <Typography>{contact?.email || '-'}</Typography>
                            </Box>
                        </Stack>

                        {contact?.phone && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    {isArabic ? 'الهاتف' : 'Phone'}
                                </Typography>
                                <Typography>{contact.phone}</Typography>
                            </Box>
                        )}

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {isArabic ? 'الرسالة' : 'Message'}
                            </Typography>
                            <Typography whiteSpace="pre-line">
                                {contact?.message || '-' }
                            </Typography>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    {isArabic ? 'تاريخ الإرسال' : 'Sent At'}
                                </Typography>
                                <Typography>{contact?.created_at ? new Date(contact.created_at).toLocaleString() : '-'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    {isArabic ? 'الموضوع' : 'Subject'}
                                </Typography>
                                <Typography>{contact?.subject || '-'}</Typography>
                            </Box>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </WrapperSection>
    );
};

export default ContactDetails;
